import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Inventory } from '@mui/icons-material';
import { Button, Card, CardContent, Chip, TextField } from '@mui/material';

import { AppHeader } from '../components/app-header';
import { BackButton } from '../components/back-button';
import { DataTable } from '../components/data-table';
import { LoadingState } from '../components/loading-state';
import { formatDateTime, formatRelativeTime } from '../utils/formatting';
import { useGetBundlesIncludingHiddenQuery } from '../store/hooks';
import type { BundleResponse } from '@store/generated/admin';

export default function BundlesListPage() {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');

  const { data, isLoading, error } = useGetBundlesIncludingHiddenQuery();

  const bundles = data?.bundles || [];

  const filteredBundles = useMemo(() => {
    if (!searchInput.trim()) return bundles;
    const searchLower = searchInput.toLowerCase();
    return bundles.filter(
      bundle =>
        bundle.name.toLowerCase().includes(searchLower) ||
        bundle.description?.toLowerCase().includes(searchLower) ||
        bundle.stripe_product_id?.toLowerCase().includes(searchLower)
    );
  }, [bundles, searchInput]);

  const handleRowClick = useCallback(
    (bundle: BundleResponse) => {
      navigate(`/admin-v2/bundle/${bundle.bundle_id}`);
    },
    [navigate]
  );

  const columns = useMemo(
    () => [
      {
        key: 'name' as const,
        label: 'Name',
        render: (value: unknown, bundle: BundleResponse) => (
          <div className="flex items-center gap-2">
            <Inventory className="h-5 w-5 text-gray-400 mr-2" />
            <span className="font-medium text-gray-900">{value as string}</span>
            {bundle.hidden && <Chip label="Hidden" size="small" variant="outlined" />}
          </div>
        ),
      },
      {
        key: 'description' as const,
        label: 'Description',
        render: (value: unknown) => (
          <span className="text-gray-600 max-w-xs truncate block">
            {(value as string) || '-'}
          </span>
        ),
      },
      {
        key: 'stripe_product_id' as const,
        label: 'Stripe Product ID',
        render: (value: unknown) => (
          <code className="text-xs bg-gray-100 px-2 py-1 rounded">{value as string}</code>
        ),
      },
      {
        key: 'grants' as const,
        label: 'Capabilities',
        render: (value: unknown, bundle: BundleResponse) => {
          const grants = bundle.grants || [];
          return (
            <div className="flex flex-wrap gap-1">
              {grants.length === 0 ? (
                <span className="text-gray-400 text-sm">None</span>
              ) : grants.length <= 2 ? (
                grants.map(grant => (
                  <Chip
                    key={grant.grant_id}
                    label={grant.capability_name || grant.capability_id}
                    size="small"
                    variant="outlined"
                  />
                ))
              ) : (
                <Chip label={`${grants.length} capabilities`} size="small" color="primary" />
              )}
            </div>
          );
        },
      },
      {
        key: 'created_at' as const,
        label: 'Created',
        render: (value: unknown) => {
          if (!value) return <span className="text-gray-400">-</span>;
          const date = new Date(value as string);
          return (
            <div className="text-sm">
              <div className="text-gray-900">{formatDateTime(date)}</div>
              <div className="text-gray-500">{formatRelativeTime(date)}</div>
            </div>
          );
        },
      },
    ],
    []
  );

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Error loading bundles</h2>
            <p className="text-gray-600 mt-2">Unable to fetch bundles list.</p>
            <Button onClick={() => navigate('/admin-v2')} className="mt-4">
              Back to Search
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <BackButton fallbackUrl="/admin-v2" />
        </div>
        <div className="mb-6">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Bundles</h1>
          </div>

          <Card className="mb-6">
            <CardContent>
              <TextField
                fullWidth
                placeholder="Search bundles by name, description, or Stripe product ID..."
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                size="small"
              />
            </CardContent>
          </Card>

          {isLoading && !data ? (
            <LoadingState message="Loading bundles..." />
          ) : (
            <DataTable
              title="Bundles"
              columns={columns}
              data={filteredBundles}
              total={filteredBundles.length}
              loading={isLoading}
              onRowClick={handleRowClick}
              emptyMessage="No bundles found"
            />
          )}
        </div>
      </main>
    </div>
  );
}
